import { DialogComponent } from "@theme/dialog";

/**
 * Hash that any link on the page can point at to open the panel.
 * `#free-gift-panel` opens with the first footer (build your own box);
 * `#free-gift-panel-<name>` opens with the footer whose `data-footer` is `<name>`.
 */
const HASH = "#free-gift-panel";

/** Drag distance (px) on the mobile sheet that counts as a dismiss. */
const DISMISS_DISTANCE = 80;

/**
 * Free gift side panel. Desktop: right drawer. Mobile: bottom sheet.
 *
 * Opens from any anchor whose href contains `#free-gift-panel`, so merchants
 * wire it up from the theme editor (announcement banner link, rich text link)
 * without touching code. The footer shown depends on the hash suffix.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} sheet
 * @property {HTMLElement} [handle]
 *
 * @extends DialogComponent
 */
class FreeGiftDrawer extends DialogComponent {
  requiredRefs = ["dialog", "sheet"];

  /** @type {string} */
  #activeFooter = "";

  #dragStartY = 0;
  #dragging = false;

  /** @type {HTMLElement | null} the node the pointer listeners were attached to */
  #handle = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("click", this.#handleDocumentClick);
    this.#decorateTriggerLinks();

    if (window.Shopify?.designMode) {
      document.addEventListener("shopify:section:select", this.#handleSectionSelect);
      document.addEventListener("shopify:section:deselect", this.#handleSectionDeselect);
    }

    const handle = this.refs.handle;
    if (handle instanceof HTMLElement) {
      this.#handle = handle;
      handle.addEventListener("pointerdown", this.#handlePointerDown);
      handle.addEventListener("pointermove", this.#handlePointerMove);
      handle.addEventListener("pointerup", this.#handlePointerUp);
      handle.addEventListener("pointercancel", this.#handlePointerUp);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("click", this.#handleDocumentClick);
    document.removeEventListener("shopify:section:select", this.#handleSectionSelect);
    document.removeEventListener("shopify:section:deselect", this.#handleSectionDeselect);

    const handle = this.#handle;
    if (handle) {
      handle.removeEventListener("pointerdown", this.#handlePointerDown);
      handle.removeEventListener("pointermove", this.#handlePointerMove);
      handle.removeEventListener("pointerup", this.#handlePointerUp);
      handle.removeEventListener("pointercancel", this.#handlePointerUp);
      this.#handle = null;
    }
  }

  /**
   * Opens the panel with the requested footer.
   *
   * @param {string} [footer] - value of a footer's `data-footer` attribute
   */
  open(footer = "") {
    this.#showFooter(footer);
    this.showDialog();
    this.#track(this.#activeFooter);
  }

  /**
   * Theme editor: the panel is invisible until opened, so open it when the
   * merchant selects the section and close it when they move on.
   *
   * @param {Event} event
   */
  #handleSectionSelect = (event) => {
    if (event.target !== this.closest(".shopify-section")) return;
    this.#showFooter(this.#activeFooter);
    this.showDialog();
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (event.target !== this.closest(".shopify-section")) return;
    this.closeDialog();
  };

  /** Used by theme-editor.js to restore the open state after a re-render. */
  get isOpen() {
    return this.refs.dialog.open;
  }

  /**
   * Rich text links cannot carry attributes, so mark every hash-matched anchor
   * as opening a dialog for assistive tech.
   */
  #decorateTriggerLinks() {
    for (const anchor of document.querySelectorAll(`a[href*="${HASH}"]`)) {
      if (!(anchor instanceof HTMLAnchorElement)) continue;
      if (this.#footerFromAnchor(anchor) === null) continue;
      anchor.setAttribute("aria-haspopup", "dialog");
    }
  }

  /**
   * Resolves which footer a trigger link asks for. Only links that point at
   * the current page count; `/pages/gifts#free-gift-panel` is a normal
   * navigation and must not be intercepted.
   *
   * @param {HTMLAnchorElement} anchor
   * @returns {string | null} footer name ("" for the default), or null when
   *   the anchor is not a panel trigger on this page
   */
  #footerFromAnchor(anchor) {
    if (anchor.target && anchor.target !== "_self") return null;

    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch {
      return null;
    }

    if (url.origin !== window.location.origin) return null;
    if (url.pathname !== window.location.pathname) return null;

    const hash = url.hash;
    if (hash === HASH) return "";
    if (hash.startsWith(`${HASH}-`)) return hash.slice(HASH.length + 1);
    return null;
  }

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const anchor = target.closest(`a[href*="${HASH}"]`);
    if (!(anchor instanceof HTMLAnchorElement)) return;

    // Let modified clicks (new tab, new window) behave like any other link.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button !== 0) return;

    const footer = this.#footerFromAnchor(anchor);
    if (footer === null) return;

    event.preventDefault();
    this.open(footer);
  };

  /**
   * Shows the footer matching `name`, hides the others. Falls back to the
   * first footer when nothing matches so the CTA never disappears.
   *
   * @param {string} name
   */
  #showFooter(name) {
    const footers = Array.from(this.querySelectorAll("[data-footer]")).filter(
      (footer) => footer instanceof HTMLElement,
    );
    if (footers.length === 0) return;

    const match =
      footers.find((footer) => footer.getAttribute("data-footer") === name) ??
      footers[0];
    if (!match) return;

    for (const footer of footers) {
      footer.hidden = footer !== match;
    }

    this.#activeFooter = match.getAttribute("data-footer") ?? "";
  }

  /**
   * Mirrors the shape src/utils/analytics.ts pushes for data-gr-element-name
   * clicks, on the same ElevarDataLayer that pipeline writes to, so GA4 and
   * Shoplift can read the open event even when the trigger is a rich text
   * link that cannot carry data attributes.
   *
   * @param {string} footer
   */
  #track(footer) {
    const w = /** @type {any} */ (window);
    if (!Array.isArray(w.ElevarDataLayer)) w.ElevarDataLayer = [];

    w.ElevarDataLayer.push({
      event: "gr_cta_clicked",
      element_name: "free_gift_panel_open",
      panel_footer: footer,
      hit_timestamp_ms: Date.now(),
    });
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;

    this.#dragging = true;
    this.#dragStartY = event.clientY;
    this.refs.sheet.classList.add("free-gift-drawer__sheet--dragging");

    this.#handle?.setPointerCapture(event.pointerId);
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (!this.#dragging) return;

    const delta = Math.max(0, event.clientY - this.#dragStartY);
    this.refs.sheet.style.setProperty("--free-gift-drag", `${delta}px`);
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (!this.#dragging) return;
    this.#dragging = false;

    const delta = Math.max(0, event.clientY - this.#dragStartY);
    const { sheet } = this.refs;

    sheet.classList.remove("free-gift-drawer__sheet--dragging");
    sheet.style.removeProperty("--free-gift-drag");

    if (delta > DISMISS_DISTANCE) this.closeDialog();
  };
}

if (!customElements.get("free-gift-drawer")) {
  customElements.define("free-gift-drawer", FreeGiftDrawer);
}
